import {Button, Dialog, DialogBackdrop, DialogPanel, DialogTitle, Input, Select} from "@headlessui/react";
import {useEffect, useState} from "react";
import {ArrowPathIcon, PlusCircleIcon, TrashIcon} from "@heroicons/react/24/outline";
import {db} from "../../firebase/firebase";
import {useAuth} from "../../contexts/AuthProvider";
import { format } from 'date-fns'; // if not installed, run: npm install date-fns

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const Products = () => {

    const { currentUser } = useAuth();

    const [open, setOpen] = useState(false);

    const [name, setName] = useState('');
    const [unitPrice, setUnitPrice] = useState('');
    const [quantity, setQuantity] = useState('');
    const [category, setCategory] = useState('');
    const [barcode, setBarcode] = useState('');

    const lastUpdated = localStorage.getItem(`inventory_last_updated_${currentUser.uid}`);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const collectionRef = db.collection('Inventory');

        const parsedFormData = {
            quantity: parseFloat(quantity),
            name: name,
            unitPrice: parseFloat(unitPrice),
            category: category,
            barcode: barcode,
        };

        try {
            // Add the new document to Firestore
            await collectionRef.add(parsedFormData);

            console.log('Item added successfully!');
            // await onUpdate();


            setQuantity("");
            setCategory("");
            setName("");
            setUnitPrice("");

            setOpen(false);

            // clearForm(); // Clear the form after submission
        } catch (error) {
            console.error('Error adding/item: ', error);
        }
    };

    const [inventory, setInventory] = useState([]);

    useEffect(() => {
        const fetchInventory = async () => {
            if (currentUser) {
                try {
                    const storedInventoryData = localStorage.getItem(`inventory_${currentUser.uid}`);
                    if (storedInventoryData) {
                        const inventoryData = JSON.parse(storedInventoryData);
                        setInventory(inventoryData);
                    } else {
                        const inventoryCollectionRef = db.collection('Inventory');
                        const snapshot = await inventoryCollectionRef.get();

                        if (!snapshot.empty) {
                            const inventoryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                            setInventory(inventoryData);
                            localStorage.setItem(`inventory_${currentUser.uid}`, JSON.stringify(inventoryData));
                        } else {
                            console.log('No inventory found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching inventory:', error);
                }
            }
        };

        fetchInventory();
    }, [currentUser]);

    const onUpdate = async () => {
        const inventoryCollectionRef = db.collection('Inventory');
        const snapshot = await inventoryCollectionRef.get();

        if (!snapshot.empty) {
            const inventoryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setInventory(inventoryData);
            localStorage.setItem(`inventory_${currentUser.uid}`, JSON.stringify(inventoryData));


            // Format and save last updated timestamp
            const now = new Date();
            const formatted = format(now, 'MMM dd, yyyy \'at\' hh:mm a');
            localStorage.setItem(`inventory_last_updated_${currentUser.uid}`, formatted);

        } else {
            console.log('No inventory found');
        }
    };

    const deleteItem = async (id) => {
        try {
            await db.collection('Inventory').doc(id).delete();

            await onUpdate();

            console.log(`Item with id ${id} deleted`);
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };


    return (
        <div className="">
            <div className="p-4 animate-fadeIn border rounded-lg bg-white shadow-lg h-[calc(100dvh-4rem)] overflow-y-auto sm:p-6 lg:p-8">


                {/*Header*/}
                <div className="sm:flex sm:items-center">
                    <div className="sm:flex-auto">
                        <h1 className="text-base flex items-center font-semibold text-gray-900">Item Inventory [{inventory.length}]

                            <Button onClick={onUpdate} className={`ml-3`}>
                                <ArrowPathIcon className={`size-4 text-gray-500 stroke-2 hover:stroke-2 hover:rotate-12`}/>
                            </Button>
                        </h1>
                        <p className="mt-2 text-sm text-gray-700">
                            This is all of the <strong className="font-semibold text-gray-900">Items</strong> you have in stock.
                            The last time this was updated was <span className={`font-semibold`}>{lastUpdated}</span>
                        </p>
                    </div>
                    <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                        <Button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="block rounded-md bg-blue-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        >
                            Add New Item
                        </Button>
                    </div>
                </div>


                {/*Body*/}
                <div className="-mx-4 mt-10 ring-1 h-[calc(100dvh-14rem)] overflow-y-scroll ring-gray-300 sm:mx-0 sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                        <thead>
                        <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                Name
                            </th>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                Barcode
                            </th>
                            <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                                Unit Price
                            </th>
                            <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                                Quantity
                            </th>
                            <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                                Category
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                Total Value
                            </th>
                            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                <span className="sr-only">Select</span>
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {inventory.map((item, planIdx) => (
                            <tr key={item.id}>
                                <td
                                    className={classNames(
                                        planIdx === 0 ? '' : 'border-t border-transparent',
                                        'relative py-4 pl-4 pr-3 text-sm sm:pl-6',
                                    )}
                                >
                                    <div className="font-medium text-gray-900">
                                        {item.name}
                                        {/*{plan.isCurrent ? <span className="ml-1 text-indigo-600">(Current Plan)</span> : null}*/}
                                    </div>
                                    <div className="mt-1 flex flex-col text-gray-500 sm:block lg:hidden">
                                        <span>
                                          ${item.unitPrice} / {item.quantity}
                                        </span>
                                        <span className="hidden sm:inline">·</span>
                                        <span>{item.category}</span>
                                    </div>
                                    {planIdx !== 0 ? <div className="absolute -top-px left-6 right-0 h-px bg-gray-200" /> : null}
                                </td>
                                <td
                                    className={classNames(
                                        planIdx === 0 ? '' : 'border-t border-gray-200',
                                        'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell',
                                    )}
                                >
                                    {item.barcode || "--"}
                                </td>
                                <td
                                    className={classNames(
                                        planIdx === 0 ? '' : 'border-t border-gray-200',
                                        'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell',
                                    )}
                                >
                                    ${item.unitPrice}
                                </td>
                                <td
                                    className={classNames(
                                        planIdx === 0 ? '' : 'border-t border-gray-200',
                                        'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell',
                                    )}
                                >
                                    {item.quantity}
                                </td>
                                <td
                                    className={classNames(
                                        planIdx === 0 ? '' : 'border-t border-gray-200',
                                        'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell',
                                    )}
                                >
                                    {item.category}
                                </td>
                                <td
                                    className={classNames(
                                        planIdx === 0 ? '' : 'border-t border-gray-200',
                                        'px-3 py-3.5 text-sm text-gray-500',
                                    )}
                                >
                                    <div className="sm:hidden">${(item.unitPrice * item.quantity)}</div>
                                    <div className="hidden sm:block">${(item.unitPrice * item.quantity)}</div>
                                </td>
                                <td
                                    className={classNames(
                                        planIdx === 0 ? '' : 'border-t border-transparent',
                                        'relative py-3.5 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 flex items-center justify-end',
                                    )}
                                >
                                    <Button
                                        type="button"
                                        // disabled={plan.isCurrent}
                                        className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                                    >
                                        Update
                                    </Button>

                                    <Button
                                        type="button"
                                        // disabled={plan.isCurrent}
                                        onClick={() => deleteItem(item.id)}
                                        className="inline-flex items-center pl-5 py-1.5 text-sm font-semibold text-gray-900 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                                    >
                                        <TrashIcon className={`size-5 text-red-500 hover:stroke-2`}/>
                                    </Button>

                                    {planIdx !== 0 ? <div className="absolute -top-px left-0 right-6 h-px bg-gray-200" /> : null}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>


            </div>

            <Dialog open={open} onClose={setOpen} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                />

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                        >
                            <div className="sm:flex sm:items-start">
                                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:size-10">
                                    <PlusCircleIcon aria-hidden="true" className="size-6 text-blue-600" />
                                </div>
                                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                    <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                                        Add Item to the Cloud
                                    </DialogTitle>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            This will Add a new item to your inventory. From here you will be able to pull that product into your order form.
                                        </p>
                                        <div className={`grid grid-cols-6 mt-3 gap-4`}>

                                            {/*Name*/}
                                            <div className="col-span-3 relative">
                                                <label
                                                    className="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900"
                                                >
                                                    Name
                                                </label>
                                                <Input
                                                    type="text"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    placeholder="Item Name"
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                                />
                                            </div>

                                            {/*Barcode*/}
                                            <div className="col-span-3 relative">
                                                <label
                                                    className="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900"
                                                >
                                                    Barcode
                                                </label>
                                                <Input
                                                    type="text"
                                                    value={barcode}
                                                    onChange={(e) => setBarcode(e.target.value)}
                                                    placeholder="Item Name"
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                                />
                                            </div>

                                            {/*Unit Price*/}
                                            <div className="col-span-2 relative">
                                                <label
                                                    className="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900"
                                                >
                                                    Unit Price
                                                </label>
                                                <Input
                                                    type="number"
                                                    value={unitPrice}
                                                    onChange={(e) => setUnitPrice(e.target.value)}
                                                    placeholder="Unit Price"
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                                />
                                            </div>

                                            {/*Quantity*/}
                                            <div className="col-span-2 relative">
                                                <label
                                                    className="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900"
                                                >
                                                    Quantity
                                                </label>
                                                <Input
                                                    type="number"
                                                    value={quantity}
                                                    onChange={(e) => setQuantity(e.target.value)}
                                                    placeholder="Quantity"
                                                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                                />
                                            </div>

                                            {/*Category*/}
                                            <div className="col-span-2 relative">
                                                <label
                                                    className="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900"
                                                >
                                                    Category
                                                </label>
                                                <Select
                                                    type="text"
                                                    value={category}
                                                    onChange={(e) => setCategory(e.target.value)}
                                                    placeholder="Category"
                                                    className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                                                >
                                                    <option value="">Select Category...</option>
                                                    <option value="Clothing">Clothing</option>
                                                    <option value="Shoes">Shoes</option>
                                                    <option value="Baby">Baby</option>
                                                    <option value="Bedding">Bedding</option>
                                                    <option value="Misc">Misc.</option>
                                                </Select>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                <Button
                                    type="button"
                                    // onClick={() => setOpen(false)}
                                    onClick={handleSubmit}
                                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-50 sm:ml-3 sm:w-auto"
                                >
                                Add Item
                                </Button>
                                <Button
                                    type="button"
                                    data-autofocus
                                    onClick={() => setOpen(false)}
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>

        </div>
    )
}

export default Products;
